import {
  Box,
  Container,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { motion } from "framer-motion";
import CTAButton from "./CTAButton";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";

const HeroBanner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const floatingAnimation = {
    y: [0, -20, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut",
    },
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: { xs: "90vh", md: "95vh" },
        display: "flex",
        alignItems: "center",
        backgroundImage:
          "url(https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=1920&q=80)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: { xs: "scroll", md: "fixed" }, // Fixed for desktop only
        backgroundRepeat: "no-repeat",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background:
            "linear-gradient(135deg, rgba(34, 51, 95, 0.85) 0%, rgba(118, 75, 162, 0.8) 100%)",
        },
      }}
    >
      {/* Animated Background Elements */}
      <motion.div
        style={{
          position: "absolute",
          top: "15%",
          left: "8%",
          width: 120,
          height: 120,
          borderRadius: "50%",
          background: "rgba(255,255,255,0.1)",
          display: { xs: "none", md: "block" }, // Hide on mobile
        }}
        animate={floatingAnimation}
      />

      <motion.div
        style={{
          position: "absolute",
          bottom: "25%",
          right: "12%",
          width: 80,
          height: 80,
          borderRadius: "30%",
          background: "rgba(255,255,255,0.08)",
          rotate: 45,
          display: { xs: "none", md: "block" }, // Hide on mobile
        }}
        animate={{
          ...floatingAnimation,
          transition: { ...floatingAnimation.transition, delay: 2 },
        }}
      />

      <Container sx={{ position: "relative", zIndex: 2 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={8}>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <Typography
                variant="h1"
                sx={{
                  color: "white",
                  fontWeight: 800,
                  mb: 3,
                  fontSize: { xs: "2.5rem", md: "4rem" },
                  lineHeight: 1.1,
                  textAlign: { xs: "center", md: "left" },
                  background:
                    "linear-gradient(45deg, #FFFFFF 30%, #E3F2FD 90%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  textShadow: "0 4px 20px rgba(0,0,0,0.1)",
                }}
              >
                Professional Home & Commercial Cleaning
              </Typography>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    color: "rgba(255,255,255,0.95)",
                    mb: 4,
                    fontWeight: 400,
                    lineHeight: 1.7,
                    fontSize: { xs: "1.1rem", md: "1.25rem" },
                    maxWidth: "90%",
                    textAlign: { xs: "center", md: "left" },
                  }}
                >
                  Blossom City Cleaning Services — Your trusted partner for
                  exceptional cleaning solutions. Fast, reliable, and
                  eco-friendly services across Kampala.
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 3,
                    flexWrap: "wrap",
                    mb: 5,
                    alignItems: "center",
                    justifyContent: { xs: "center", md: "flex-start" },
                  }}
                >
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <CTAButton
                      to="/contact"
                      size="large"
                      sx={{
                        background: "linear-gradient(45deg, #FF6B35, #F7931E)",
                        color: "white",
                        fontWeight: 700,
                        px: { xs: 3, md: 4 },
                        py: 1.5,
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        boxShadow: "0 8px 32px rgba(255, 107, 53, 0.4)",
                        "&:hover": {
                          background:
                            "linear-gradient(45deg, #E55A2B, #E0821A)",
                          boxShadow: "0 12px 40px rgba(255, 107, 53, 0.6)",
                        },
                      }}
                    >
                      Book Now
                    </CTAButton>
                  </motion.div>

                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <CTAButton
                      to="/contact"
                      variant="outlined"
                      size="large"
                      sx={{
                        color: "white",
                        borderColor: "white",
                        borderWidth: 2,
                        fontWeight: 600,
                        px: { xs: 3, md: 4 },
                        py: 1.5,
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        backdropFilter: "blur(10px)",
                        "&:hover": {
                          borderColor: "white",
                          backgroundColor: "rgba(255,255,255,0.15)",
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      Get a Quote
                    </CTAButton>
                  </motion.div>
                </Box>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: 2, md: 4 },
                    flexWrap: "wrap",
                    alignItems: "center",
                    background: "rgba(255,255,255,0.1)",
                    backdropFilter: "blur(10px)",
                    borderRadius: 3,
                    p: { xs: 2, md: 3 },
                    border: "1px solid rgba(255,255,255,0.2)",
                    maxWidth: "fit-content",
                    mx: { xs: "auto", md: 0 },
                    justifyContent: { xs: "center", md: "flex-start" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <motion.div
                      animate={{
                        scale: [1, 1.2, 1],
                        transition: {
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                        },
                      }}
                    >
                      <PhoneIcon
                        sx={{ color: "#4CAF50", fontSize: { xs: 24, md: 28 } }}
                      />
                    </motion.div>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "white",
                        fontWeight: 700,
                        fontSize: { xs: "0.9rem", md: "1.1rem" },
                      }}
                    >
                      0752 058 190
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <motion.div
                      animate={{
                        scale: [1, 1.2, 1],
                        transition: {
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: 1,
                        },
                      }}
                    >
                      <PhoneIcon
                        sx={{ color: "#2196F3", fontSize: { xs: 24, md: 28 } }}
                      />
                    </motion.div>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "white",
                        fontWeight: 700,
                        fontSize: { xs: "0.9rem", md: "1.1rem" },
                      }}
                    >
                      0200 940 830
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <motion.div
                      animate={{
                        scale: [1, 1.2, 1],
                        transition: {
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut",
                          delay: 2,
                        },
                      }}
                    >
                      <LocationOnIcon
                        sx={{ color: "#FF6B35", fontSize: { xs: 24, md: 28 } }}
                      />
                    </motion.div>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "white",
                        fontWeight: 700,
                        fontSize: { xs: "0.8rem", md: "1rem" },
                      }}
                    >
                      Kololo — Opposite Libya Embassy
                    </Typography>
                  </Box>
                </Box>
              </motion.div>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        style={{
          position: "absolute",
          bottom: 30,
          left: "50%",
          transform: "translateX(-50%)",
        }}
      >
        <motion.div
          animate={{
            y: [0, 10, 0],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          style={{
            color: "white",
            textAlign: "center",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255,255,255,0.8)",
              fontWeight: 600,
              mb: 1,
              fontSize: { xs: "0.8rem", md: "0.875rem" },
            }}
          >
            Scroll to Explore
          </Typography>
          <Box
            sx={{
              width: 2,
              height: 30,
              background: "white",
              borderRadius: 1,
              mx: "auto",
            }}
          />
        </motion.div>
      </motion.div>
    </Box>
  );
};

export default HeroBanner;
